/**
 * BulkValidateModal - Livraison en masse avec preview groupé VENTE + ACHAT
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { CheckCircle, AlertTriangle, Loader2, X, Check, Download, Eye, ArrowLeft, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

export const BulkValidateModal = ({ isOpen, onClose, onSuccess, project = "WORLD CUP 2026" }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [step, setStep] = useState('input'); // 'input' | 'preview' | 'result'
  const [previewData, setPreviewData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);

  const extractRefs = () => {
    return text
      .split(/[,;\s\n\r]+/)
      .map(r => r.trim().toUpperCase())
      .filter(r => r.length > 0);
  };

  // Step 1 → Step 2: Preview
  const handlePreview = async () => {
    const refs = extractRefs();
    if (refs.length === 0) {
      toast.error('Aucune référence valide');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/preview-livraison`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ references: refs, project })
      });
      const data = await res.json();

      if (data.success) {
        setPreviewData(data);
        // Auto-select only groups WITH achats and NOT already validated
        const autoSelected = new Set();
        (data.groups || []).forEach(g => {
          if (g.has_achats && !g.already_validated) {
            autoSelected.add(g.vente.id);
          }
        });
        setSelected(autoSelected);
        setStep('preview');

        if (data.without_achats > 0) {
          toast.warning(`${data.without_achats} VENTE(s) SANS ACHAT associé !`);
        }
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 → Step 3: Validate selected
  const handleValidate = async () => {
    if (selected.size === 0) {
      toast.error('Sélectionnez au moins une transaction');
      return;
    }

    // Get ref_vente from selected IDs
    const selectedRefs = (previewData?.groups || [])
      .filter(g => selected.has(g.vente.id))
      .map(g => g.vente.ref_vente);

    setValidating(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/bulk-validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ references: selectedRefs, project })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStep('result');
        toast.success(`${data.validated_count} transaction(s) validée(s)`);
        if (onSuccess) onSuccess();
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setValidating(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExportCSV = () => {
    if (!previewData?.groups) return;
    const rows = [];
    rows.push(['Type', 'Ref Vente', 'Ref Achat', 'Événement', 'Catégorie', 'Source', 'Qty', 'Prix', 'Client', 'Email', 'Block', 'Rang', 'Place', 'Statut', 'ACHAT liés']);

    for (const g of previewData.groups) {
      const v = g.vente;
      rows.push(['VENTE', v.ref_vente, v.ref_achat, v.evenement, v.categorie, v.source, v.qty, v.prix, v.nom_client, v.mail_client, '', '', '', v.statut, g.achat_count]);
      for (const a of g.achats) {
        rows.push(['ACHAT', '', a.ref_achat, a.evenement, a.categorie, a.source, 1, a.prix, a.nom_client, a.mail_client, a.block, a.rang, a.place, '', '']);
      }
    }

    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `preview_livraison_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté !');
  };

  const handleClose = () => {
    setText('');
    setStep('input');
    setPreviewData(null);
    setSelected(new Set());
    setResult(null);
    onClose();
  };

  const goBack = () => {
    setStep('input');
    setPreviewData(null);
    setSelected(new Set());
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-[#C4A052]/30 text-white max-w-6xl max-h-[85vh] flex flex-col" data-testid="bulk-validate-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Livraison en masse
            {step === 'preview' && <span className="text-sm font-normal text-zinc-400 ml-2">— Aperçu groupé</span>}
            {step === 'result' && <span className="text-sm font-normal text-zinc-400 ml-2">— Résultat</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3">

          {/* ===== STEP 1: INPUT ===== */}
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Collez vos références de transaction (une par ligne ou séparées par des virgules)
                </label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={"REF001, REF002, REF003\nou\nREF001\nREF002\nREF003"}
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[150px] font-mono text-sm"
                  disabled={loading}
                  data-testid="refs-textarea"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {extractRefs().length} référence(s) détectée(s)
                </p>
              </div>
            </div>
          )}

          {/* ===== STEP 2: PREVIEW GROUPÉ ===== */}
          {step === 'preview' && previewData && (
            <>
              {/* Stats bar */}
              <div className="flex items-center justify-between gap-2 flex-wrap bg-zinc-800 rounded-lg p-3">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-zinc-300">
                    <strong>{previewData.total}</strong> VENTE trouvée(s)
                  </span>
                  <span className="text-green-400">
                    <Package className="h-3.5 w-3.5 inline mr-1" />
                    {previewData.with_achats} avec ACHAT
                  </span>
                  {previewData.without_achats > 0 && (
                    <span className="text-red-400 font-medium">
                      <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                      {previewData.without_achats} SANS ACHAT
                    </span>
                  )}
                  {previewData.already_validated > 0 && (
                    <span className="text-blue-400">
                      {previewData.already_validated} déjà validée(s)
                    </span>
                  )}
                  {previewData.not_found_count > 0 && (
                    <span className="text-yellow-400">
                      {previewData.not_found_count} non trouvée(s)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportCSV} className="border-zinc-600 text-zinc-300" data-testid="export-preview-csv">
                    <Download className="h-4 w-4 mr-1" /> CSV
                  </Button>
                  <span className="text-sm text-zinc-400">{selected.size} sélectionnée(s)</span>
                </div>
              </div>

              {/* Not found refs */}
              {previewData.not_found_count > 0 && (
                <div className="text-sm text-yellow-400 bg-yellow-500/10 rounded p-2 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Non trouvées : {previewData.not_found.slice(0, 10).join(', ')}{previewData.not_found.length > 10 ? ` ...+${previewData.not_found.length - 10}` : ''}</span>
                </div>
              )}

              {/* Grouped table */}
              <div className="overflow-auto flex-1 space-y-1" data-testid="preview-groups">
                {previewData.groups.map((group) => {
                  const v = group.vente;
                  const isOrphan = !group.has_achats;
                  const isAlready = group.already_validated;
                  const isSelected = selected.has(v.id);

                  return (
                    <div
                      key={v.id}
                      className={`rounded-lg border overflow-hidden ${
                        isOrphan
                          ? 'border-red-500/60 bg-red-950/20'
                          : isAlready
                            ? 'border-blue-500/40 bg-blue-950/10'
                            : 'border-[#C4A052]/30 bg-zinc-800/50'
                      }`}
                      data-testid={`group-${v.id}`}
                    >
                      {/* VENTE row */}
                      <div className={`flex items-center gap-2 px-3 py-2 ${
                        isOrphan ? 'bg-red-900/30' : isAlready ? 'bg-blue-900/20' : 'bg-zinc-800'
                      }`}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(v.id)}
                          disabled={isOrphan || isAlready}
                          data-testid={`select-${v.id}`}
                        />
                        <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-pink-600/80 text-white">VENTE</span>
                        <span className="font-mono text-xs text-white">{v.ref_vente}</span>
                        <span className="text-xs text-zinc-300 truncate max-w-[200px]">{v.evenement}</span>
                        <span className="text-xs text-zinc-400">{v.categorie}</span>
                        <span className="text-xs text-zinc-400">x{v.qty}</span>
                        <span className="text-xs text-[#C4A052] font-medium">{v.prix ? `${v.prix}€` : ''}</span>
                        <span className="text-xs text-zinc-400">{v.source || ''}</span>
                        {v.nom_client && <span className="text-xs text-zinc-400">{v.nom_client}</span>}
                        <div className="ml-auto flex items-center gap-2">
                          {isOrphan && (
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white" data-testid={`no-achat-${v.id}`}>
                              SANS ACHAT
                            </span>
                          )}
                          {isAlready && (
                            <span className="px-2 py-0.5 rounded text-xs bg-blue-500/30 text-blue-300">
                              {v.statut}
                            </span>
                          )}
                          <span className="text-xs text-zinc-500">{group.achat_count} ACHAT</span>
                        </div>
                      </div>

                      {/* ACHAT rows */}
                      {group.achats.length > 0 && (
                        <div className="border-t border-zinc-700/50">
                          {group.achats.map((a) => (
                            <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 pl-10 bg-zinc-900/50 border-b border-zinc-800/50 last:border-0">
                              <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-emerald-600/80 text-white">ACHAT</span>
                              <span className="font-mono text-xs text-zinc-400">{a.ref_achat}</span>
                              <span className="text-xs text-zinc-400 truncate max-w-[160px]">{a.evenement}</span>
                              <span className="text-xs text-zinc-500">{a.categorie}</span>
                              {a.block && <span className="text-xs text-zinc-500">B{a.block}</span>}
                              {a.rang && <span className="text-xs text-zinc-500">R{a.rang}</span>}
                              {a.place && <span className="text-xs text-zinc-500">P{a.place}</span>}
                              <span className="text-xs text-[#C4A052]">{a.prix ? `${a.prix}€` : ''}</span>
                              {a.nom_client && <span className="text-xs text-zinc-400">{a.nom_client}</span>}
                              {a.email_compte && <span className="text-xs text-zinc-500 truncate max-w-[140px]">{a.email_compte}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ===== STEP 3: RESULT ===== */}
          {step === 'result' && result && (
            <div className="bg-zinc-800 rounded-lg p-6 space-y-3">
              {result.validated_count > 0 && (
                <div className="flex items-center gap-2 text-green-400 text-lg">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">{result.validated_count} transaction(s) validée(s)</span>
                </div>
              )}
              {result.validated_count > 0 && (
                <div className="text-sm text-zinc-400 space-y-1">
                  <p>{result.vente_updated} VENTE mise(s) à jour</p>
                  <p>{result.achat_updated} ACHAT mis à jour</p>
                </div>
              )}
              {result.skipped_no_source_count > 0 && (
                <div className="text-sm text-red-400" data-testid="skipped-no-source">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {result.skipped_no_source_count} rejetée(s) — source manquante
                </div>
              )}
              {result.skipped_not_attribue_count > 0 && (
                <div className="text-sm text-orange-400" data-testid="skipped-not-attribue">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {result.skipped_not_attribue_count} rejetée(s) — non attribuée(s)
                </div>
              )}
              {result.already_validated_count > 0 && (
                <div className="text-sm text-blue-400" data-testid="already-validated">
                  {result.already_validated_count} déjà validée(s)
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          {step === 'preview' && (
            <Button variant="outline" onClick={goBack} className="border-zinc-700" data-testid="back-to-input">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} className="border-zinc-700">
            <X className="h-4 w-4 mr-2" />
            Fermer
          </Button>

          {step === 'input' && (
            <Button
              onClick={handlePreview}
              disabled={loading || !text.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="preview-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Chargement...</>
              ) : (
                <><Eye className="h-4 w-4 mr-2" /> Aperçu groupé</>
              )}
            </Button>
          )}

          {step === 'preview' && (
            <Button
              onClick={handleValidate}
              disabled={validating || selected.size === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="validate-btn"
            >
              {validating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Livraison...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" /> Livrer {selected.size > 0 ? `(${selected.size})` : ''}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkValidateModal;
